import { useTerminalsState } from "@/States/Terminals";
import { useConfig } from "@/api/hooks/useConfig";
import { BuildingSelector } from "@/components/v2/BuildingSelector";
import { DatePicker } from "@/components/v2/DatePicker";
import type { PresetKey } from "@/components/v2/DatePicker/constants";
import { getTimeFilter } from "@/components/v2/DatePicker/helpers";
import { TenantCategoriesSelector } from "@/components/v2/TenantCategoriesSelector";

import { useCallback, useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";

import { ReportingTableView } from "./components/reportingTable";
import { ReportingHeader } from "./components/reportingTitleHeader";
import { ReportingViewSelector } from "./components/reportingViewSelector";
import { ReportingTabView } from "./components/tabsView";
import { ReportingPageContext } from "./context";
import { REPORTING_TAB_TYPES, REPORTING_VIEW_TYPES } from "./types";

export const ReportingV2 = () => {
  const { isRealEstate } = useConfig();
  const { terminals } = useTerminalsState();
  const [dateRange, setDateRange] = useState<DateRange>();
  const [datePreset, setDatePreset] = useState<PresetKey>();
  const [selectedTenantCategoryIds, setSelectedTenantCategoryIds] = useState<
    string[] | null
  >(null);
  const [selectedBuildingIds, setSelectedBuildingIds] = useState<
    string[] | null
  >(null);
  const [viewType, setViewType] = useState<REPORTING_VIEW_TYPES>(
    REPORTING_VIEW_TYPES.BUILDING,
  );
  const [tabType, setTabType] = useState<REPORTING_TAB_TYPES>(
    REPORTING_TAB_TYPES.TOTAL,
  );

  const [selectorLabel, setSelectorLabel] = useState<string>("");

  const onDateAndPresetChange = useCallback(
    (range: DateRange, preset: PresetKey) => {
      setDateRange(range);
      setDatePreset(preset);
    },
    [],
  );

  const timeFilter = useMemo(() => getTimeFilter(dateRange), [dateRange]);

  const contextValue = useMemo(
    () => ({
      dateRange,
      datePreset,
      timeFilter,
      selectedTenantCategoryIds,
      selectedBuildingIds,
      viewType,
      tabType,
      selectorLabel,
    }),
    [
      dateRange,
      datePreset,
      timeFilter,
      selectedTenantCategoryIds,
      selectedBuildingIds,
      viewType,
      tabType,
      selectorLabel,
    ],
  );

  const showPortfolio = useMemo(
    () => terminals.length > 1 && isRealEstate,
    [terminals, isRealEstate],
  );

  const dataView = useMemo(
    () => (
      <ReportingPageContext.Provider value={contextValue}>
        <div className="ml-[20px]">
          <ReportingTabView setTabType={setTabType} />

          <ReportingTableView />
        </div>
      </ReportingPageContext.Provider>
    ),
    [contextValue],
  );

  // comment added for the purpose of testing hotfix release - DEVELOP

  const view = useMemo(
    () => (
      <div className="h-full min-h-[660px] w-full px-[33px] py-[44px]">
        <div className="flex flex-wrap items-center gap-6">
          <ReportingHeader viewType={viewType} />
          <div className="ml-[20px] flex flex-wrap items-center gap-3">
            {showPortfolio && (
              <ReportingViewSelector
                viewType={viewType}
                setViewType={setViewType}
                setTabType={setTabType}
              />
            )}
            {viewType === REPORTING_VIEW_TYPES.BUILDING ? (
              <TenantCategoriesSelector
                timeFilter={timeFilter}
                value={selectedTenantCategoryIds}
                onChange={setSelectedTenantCategoryIds}
                setCurrentViewLabel={setSelectorLabel}
              />
            ) : (
              <BuildingSelector
                timeFilter={timeFilter}
                value={selectedBuildingIds}
                onChange={setSelectedBuildingIds}
                setCurrentViewLabel={setSelectorLabel}
              />
            )}
            <DatePicker
              value={dateRange}
              presetValue={datePreset}
              onChange={onDateAndPresetChange}
            />
          </div>
        </div>
        {dataView}
      </div>
    ),
    [
      onDateAndPresetChange,
      dataView,
      dateRange,
      selectedTenantCategoryIds,
      setSelectedTenantCategoryIds,
      timeFilter,
      datePreset,
      viewType,
      selectedBuildingIds,
      setSelectedBuildingIds,
      showPortfolio,
    ],
  );

  return view;
};
